import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { canInlineDownload } from "@/lib/document-source";

type Document = components["schemas"]["Document"];

/**
 * Fetch a repository document's binary through the AUTHENTICATED API proxy and
 * trigger a browser save. Mirrors the blob hook's source dispatch (a raw
 * `<a href>` to the API would 401 — the Bearer token rides the apiClient
 * interceptor). Throws for cromerr documents — the only one of the six
 * sources (document/form/task/notice/cromerr/report_form_output) with no
 * Document-reachable binary; the caller disables download for it.
 */
export async function fetchAndSaveDocument(orgId: string, doc: Document): Promise<void> {
  if (!canInlineDownload(doc.source)) {
    throw new Error(`Source ${doc.source} has no inline download`);
  }
  const { data, error } = await fetchBlob(orgId, doc);
  if (error || !data) {
    throw new Error("Failed to download document");
  }
  // parseAs: "blob" returns a runtime Blob; the generated type is string.
  const blob = data as unknown as Blob;
  saveBlob(blob, doc.filename);
}

/**
 * Trigger a browser save of a blob the API client already returned.
 *
 * The object URL is revoked on a zero-delay timeout, not synchronously: the
 * browser's download pipeline reads the URL after the click handler returns, and
 * an immediate revoke can outrun it — the race `lib/download-pdf.ts` records on
 * its own anchor. Every caller that saves an api-client blob shares this
 * function, so that timing is ONE decision; it was made the deferred way because
 * the race is real and documented in this repo, not because the previously
 * immediate call sites were ever shown to be safe.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}

function fetchBlob(orgId: string, doc: Document) {
  switch (doc.source) {
    case "document":
      return apiClient.GET("/orgs/{orgId}/entity-attachments/{attachmentId}/download", {
        params: { path: { orgId, attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "form":
      return apiClient.GET("/orgs/{orgId}/form-submissions/attachments/{attachmentId}/download", {
        params: { path: { orgId, attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "task":
      return apiClient.GET("/orgs/{orgId}/tasks/{taskId}/attachments/{attachmentId}/download", {
        params: { path: { orgId, taskId: doc.context_id ?? "", attachmentId: doc.id } },
        parseAs: "blob",
      });
    case "notice":
      return apiClient.GET("/orgs/{orgId}/notices/{id}/pdf", {
        params: { path: { orgId, id: doc.id } },
        parseAs: "blob",
      });
    case "report_form_output":
      return apiClient.GET("/orgs/{orgId}/report-form-outputs/{outputId}/pdf", {
        params: { path: { orgId, outputId: doc.id } },
        parseAs: "blob",
      });
    default:
      throw new Error(`Source ${doc.source} has no inline download`);
  }
}
